import { Box } from '@mui/material';
import { type JSX, useEffect, useState } from 'react';
import { Navigate, generatePath, useParams, useSearchParams } from 'react-router';

import { TprmQuestionnaireApi } from 'Api/TPRM/TprmQuestionnaireApi';
import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { PageBackground } from 'Components/Containers/PageBackground/PageBackground';
import { PageCell } from 'Components/Containers/PageCell/PageCell';
import { PageContent } from 'Components/Containers/PageContent/PageContent';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { Modal } from 'Components/Modal/Modal';
import { Text } from 'Components/Text/Text';
import { ICON_CLOSE } from 'Config/Icons';
import { TPRM_QUESTIONNAIRE_LIST } from 'Config/PathTemplates';
import { PUBLIC } from 'Config/Paths';
import { BasicAuthHandler } from 'Helpers/Auth/BasicAuth/BasicAuthHandler';
import { isSessionForService } from 'Helpers/Auth/BasicAuth/Utils';
import { validateEmail, validatePassword } from 'Helpers/InputValidation';
import { WindowNavigator } from 'Helpers/WindowNavigator';
import { ValidationError } from 'Models/ErrorTypes';
import { PublicQuestionnaireRegistrationRequest } from 'Models/TPRM';

import styles from './VSCLogin.module.css';
import { POST_LOGIN_REDIRECT_PARAM } from '../RequirePublicDDQAuth/RequirePublicDDQAuth';

export const INVALID_PASSWORD_ERROR_MESSAGE = `Invalid password. Password must be between 8 and 128 characters and contain the following complexities based on the length of the password:\n
    - 8-11 characters: at least 1 upper case letter, 1 lower case letter, 1 number, and 1 special character.\n
    - 12-15 characters: at least 1 upper case letter, 1 lower case letter, and 1 number.\n
    - 16-19 characters: at least 1 upper case letter and 1 lower case letter.\n
    - 20+ characters: no character requirements.`;

interface UrlParams {
    service_id: string;
    third_party_id: string;
    client_id: string;
}

export interface VSCLoginProps {
    questionnaireApi: TprmQuestionnaireApi;
    basicAuthHandler: BasicAuthHandler;
    windowNavigator: WindowNavigator;
}

interface FormState {
    isSaving: boolean;
    authorized?: boolean;
    failureMessage?: string;
}

interface FormFieldsState {
    email: string;
    password: string;
    confirmPassword: string;
}

export const VSCLogin = (props: VSCLoginProps): JSX.Element => {
    const { client_id, third_party_id, service_id } = useParams<keyof UrlParams>() as UrlParams;
    const [searchParams] = useSearchParams();
    const [showLoginForm, setShowLoginForm] = useState<boolean>(true); // When false, the registration form is showed instead.
    const [formState, setFormState] = useState<FormState>({ isSaving: false });
    const [formFieldsState, setFormFieldsState] = useState<FormFieldsState>({ email: '', password: '', confirmPassword: '' });
    const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState<boolean>(false);

    /**
     * The page to send the user to after a successful log in. If they were redirected here from a page they were trying to reach (see `RequirePublicDDQAuth`), that destination is used; otherwise, the default destination is the questionnaire listing.
     */
    const getPostLoginDestination = (): string => {
        const requestedPath = searchParams.get(POST_LOGIN_REDIRECT_PARAM);
        if (requestedPath && requestedPath.startsWith(`/${PUBLIC}/`)) {
            return requestedPath;
        }
        return generatePath(TPRM_QUESTIONNAIRE_LIST, { client_id, third_party_id, service_id });
    };

    const hasStoredCredentials = props.basicAuthHandler.getBase64EncodedCredentials() !== null;
    const sessionMatchesUrl = isSessionForService(props.basicAuthHandler.getAuthenticatedSession(), client_id, third_party_id, service_id);

    useEffect(() => {
        if (hasStoredCredentials && !sessionMatchesUrl) {
            // If the user is already logged in, but has clicked on a link to this page for a different service, clear their session and keep them on this page.
            props.basicAuthHandler.logout();
        }
    }, [hasStoredCredentials, sessionMatchesUrl, props.basicAuthHandler]);

    // If the user is already logged in for this service, send them to the questionnaires listing rather than showing the login form again. This is essentially the inverse of the redirect performed by `RequirePublicDDQAuth`.
    if (sessionMatchesUrl) {
        return <Navigate to={getPostLoginDestination()} replace />;
    }

    const login = async () => {
        try {
            await props.basicAuthHandler.login(formFieldsState.email, formFieldsState.password, client_id, third_party_id, service_id);
            props.windowNavigator.navigateTo(getPostLoginDestination());
        } catch (error) {
            setFormState({ isSaving: false, failureMessage: error.message });
        }
    };

    const handleRegister = async () => {
        setFormState({ isSaving: true });

        const request: PublicQuestionnaireRegistrationRequest = {
            client_id,
            third_party_id,
            service_id,
            email_address: formFieldsState.email,
            password: formFieldsState.password,
        };

        try {
            validate();
            await props.questionnaireApi.register(request);
            login();
        } catch (error) {
            setFormState({ isSaving: false, failureMessage: error.message });
        }
    };

    const handleLogin = () => {
        setFormState({ isSaving: true });
        try {
            login();
        } catch (error) {
            setFormState({ isSaving: false, failureMessage: error.message });
        }
    };

    const validate = () => {
        if (!validateEmail(formFieldsState.email)) {
            throw new ValidationError('Invalid email address.');
        }

        if (!validatePassword(formFieldsState.password)) {
            throw new ValidationError(INVALID_PASSWORD_ERROR_MESSAGE);
        }

        if (formFieldsState.password !== formFieldsState.confirmPassword) {
            throw new ValidationError('The password and confirm password fields do not match.');
        }
    };

    const onSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        if (showLoginForm) {
            handleLogin();
        } else {
            handleRegister();
        }
    };

    const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
        event.preventDefault();
        setFormFieldsState({ ...formFieldsState, [event.currentTarget.name]: event.currentTarget.value });
    };

    if (showLoginForm === true) {
        return (
            <>
                <PageBackground color="blueMountains">
                    <div className={styles.header}>
                        <PageContent>
                            <Text color="white" variant="Header1">
                                Third-Party Due Diligence Questionnaires
                            </Text>
                        </PageContent>
                    </div>
                </PageBackground>
                <PageBackground color="white">
                    <PageContent>
                        <PageCell>
                            {formState.failureMessage && <Alert type="error">{formState.failureMessage}</Alert>}
                            <Text variant="Header2">Log In</Text>
                            <Text noStyles>Don't have an account?</Text>
                            <Button onClick={() => setShowLoginForm(false)} size="sm" variant="linkText">
                                Register
                            </Button>
                            <Box component="form" noValidate onSubmit={onSubmit}>
                                <div>
                                    <FormFieldText value={formFieldsState.email} formFieldId="email" formFieldLabel="Email" handleChange={handleChange} required />
                                </div>
                                <div>
                                    <FormFieldText formFieldType="password" value={formFieldsState.password} formFieldId="password" formFieldLabel="Password" handleChange={handleChange} required />
                                </div>
                                <div>
                                    <Button onClick={() => setForgotPasswordModalOpen(true)} size="sm" variant="linkText">
                                        Forgot Password?
                                    </Button>
                                </div>
                                <div className={styles.buttonContainer}>
                                    <Button variant="submit" isLoading={formState.isSaving} loadingText="Logging in...">
                                        Log In
                                    </Button>
                                </div>
                            </Box>
                        </PageCell>
                    </PageContent>
                </PageBackground>
                {forgotPasswordModalOpen && (
                    <Modal open header="Forgot Password">
                        <Text>If you have forgotten your password, contact the individual listed in your email invitation to reset your password.</Text>
                        <Text color="red">At the discretion of the individual performing the password reset, all existing progress on questionnaires may be cleared.</Text>
                        <div className="modalFormButtonContainer">
                            <Button variant="secondary" onClick={() => setForgotPasswordModalOpen(false)} fontAwesomeImage={ICON_CLOSE}>
                                Close
                            </Button>
                        </div>
                    </Modal>
                )}
            </>
        );
    } else {
        return (
            <>
                <PageBackground color="blueMountains">
                    <div className={styles.header}>
                        <PageContent>
                            <Text color="white" variant="Header1">
                                Third-Party Due Diligence Questionnaires
                            </Text>
                        </PageContent>
                    </div>
                </PageBackground>
                <PageBackground color="white">
                    <PageContent>
                        <PageCell>
                            {formState.failureMessage && <Alert type="error">{formState.failureMessage}</Alert>}
                            <Text variant="Header2">Register</Text>
                            <Text noStyles>Already have an account?</Text>
                            <Button onClick={() => setShowLoginForm(true)} size="sm" variant="linkText">
                                Log In
                            </Button>
                            <Box component="form" noValidate onSubmit={onSubmit}>
                                <div>
                                    <FormFieldText value={formFieldsState.email} formFieldId="email" formFieldLabel="Email" handleChange={handleChange} required tooltip="Registration is limited to individuals who receive an email invitation regarding completion of this questionnaire." />
                                </div>
                                <div>
                                    <FormFieldText formFieldType="password" value={formFieldsState.password} formFieldId="password" formFieldLabel="Password" handleChange={handleChange} required tooltip="Password must be between 8 and 128 characters and contain at least three of the following: a lower-case letter, an upper-case letter, a number, a special character." />
                                </div>
                                <div>
                                    <FormFieldText formFieldType="password" value={formFieldsState.confirmPassword} formFieldId="confirmPassword" formFieldLabel="Confirm Password" handleChange={handleChange} required />
                                </div>
                                <div className={styles.buttonContainer}>
                                    <Button variant="submit" isLoading={formState.isSaving} loadingText="Registering...">
                                        Register
                                    </Button>
                                </div>
                            </Box>
                        </PageCell>
                    </PageContent>
                </PageBackground>
            </>
        );
    }
};
